import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Button,
  Typography,
  Paper,
  Grid,
  Divider,
  Chip,
  CircularProgress,
  Alert,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  LocationOn as LocationIcon,
  Business as BusinessIcon,
  Person as PersonIcon,
  Notes as NotesIcon,
} from '@mui/icons-material';
import api from '../../services/api';
import { useAuth } from '../../context/AuthContext';

const CustomerDetail: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { isAuthenticated, isLoading: authLoading, hasPermission } = useAuth();
  
  const [customer, setCustomer] = useState<any | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  useEffect(() => {
    const fetchCustomer = async () => {
      setLoading(true);
      try {
        const response = await api.get(`/customers/${id}`);
        if (response.data && response.data.success) {
          setCustomer(response.data.data);
        } else {
          setError('Failed to load customer details');
        }
      } catch (error) {
        console.error('Error fetching customer:', error);
        setError('An error occurred while loading the customer details');
      } finally {
        setLoading(false);
      }
    };
    
    if (!authLoading && isAuthenticated && id) {
      fetchCustomer();
    }
  }, [authLoading, isAuthenticated, id]);
  
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
        <CircularProgress />
      </Box>
    );
  }
  
  if (error || !customer) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{error || 'Customer not found'}</Alert>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/customers')}
          sx={{ mt: 2 }}
        >
          Back to Customers
        </Button>
      </Box>
    );
  }
  
  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/customers')}
            sx={{ mr: 2 }}
          >
            Back
          </Button>
          <Typography variant="h4">Customer Details</Typography>
        </Box>
        {hasPermission('customers.update') && (
          <Button
            variant="contained"
            color="primary"
            onClick={() => navigate(`/customers/edit/${customer.id}`)}
          >
            Edit Customer
          </Button>
        )}
      </Box>
      
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ mb: 3 }}>
          <Typography variant="h5" gutterBottom>
            {customer.company_name}
          </Typography>
          <Chip
            label={customer.is_active ? 'Active' : 'Inactive'}
            color={customer.is_active ? 'success' : 'error'}
            size="small"
          />
        </Box>
        
        <Divider sx={{ my: 2 }} />
        
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 3 }}>
          <Box sx={{ flex: 1 }}>
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                <PersonIcon fontSize="small" sx={{ mr: 1, verticalAlign: 'middle' }} />
                Contact Person
              </Typography>
              <Typography variant="body1">
                {customer.contact_name || 'N/A'}
              </Typography>
            </Box>
            
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                <EmailIcon fontSize="small" sx={{ mr: 1, verticalAlign: 'middle' }} />
                Email
              </Typography>
              <Typography variant="body1">
                {customer.email || 'N/A'}
              </Typography>
            </Box>
            
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                <PhoneIcon fontSize="small" sx={{ mr: 1, verticalAlign: 'middle' }} />
                Phone
              </Typography>
              <Typography variant="body1">
                {customer.phone || 'N/A'}
              </Typography>
            </Box>
          </Box>
          
          <Box sx={{ flex: 1 }}>
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                <LocationIcon fontSize="small" sx={{ mr: 1, verticalAlign: 'middle' }} />
                Address
              </Typography>
              <Typography variant="body1">
                {customer.address || 'N/A'}
              </Typography>
              <Typography variant="body1">
                {customer.city ? `${customer.city}${customer.state ? `, ${customer.state}` : ''}` : ''}
                {customer.postal_code ? ` ${customer.postal_code}` : ''}
              </Typography>
              <Typography variant="body1">
                {customer.country || ''}
              </Typography>
            </Box>
            
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                <BusinessIcon fontSize="small" sx={{ mr: 1, verticalAlign: 'middle' }} />
                Customer Type
              </Typography>
              <Typography variant="body1">
                {customer.type || 'Business'}
              </Typography>
            </Box>
          </Box>
        </Box>
        
        {customer.notes && (
          <>
            <Divider sx={{ my: 2 }} />
            <Box>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                <NotesIcon fontSize="small" sx={{ mr: 1, verticalAlign: 'middle' }} />
                Notes
              </Typography>
              <Typography variant="body1">
                {customer.notes}
              </Typography>
            </Box>
          </>
        )}
      </Paper>
      

    </Box>
  );
};

export default CustomerDetail;
